import { useEffect, useState } from "react";

import { InfoModal } from "@newsela/angelou";

import { getHasAssignees } from "@/assignments/utils/assignmentValidationHelpers/assignmentValidationHelpers";
import useSaveDraftAssignment from "@/assignments/utils/useSaveDraftAssignment";
import ArticleLevel from "@/types/models/ArticleLevel.interface";
import { Assignment } from "@/types/models/Assignment.interface";
import { UserProperties } from "@/types/models/UserProperties.interface";
import AssignmentSettings from "@/view/AdditionalAssignmentSettings/AdditionalAssignmentSettings";
import { HandleSave } from "@/view/AssignActionsContainer/AssignActionsContainer.interface";
import LexileLevelSettings from "@/view/AssignModal/LexileLevelSettings/LexileLevelSettings";
import contentTypes from "@/view/constants/contentTypes";
import ShareAssignmentModal from "@/view/ShareAssignmentModal";
import ShareAssignmentModalContent from "@/view/ShareAssignmentModal/ShareAssignmentModalContent";
import { getShareModalValues } from "@/view/ShareAssignmentModal/utils/getShareModalValues";
import {
  ASSIGNMENT_MODE,
  currentAssignmentMode,
} from "@/view/utils/currentAssignmentMode";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import AssignmentOptions from "./AssignmentOptions";
import { infoModalCss, rootCss, scrollableBodyCss } from "./AssignModal.style";
import Footer from "./Footer";

interface AssignModalInterface {
  assignModalProps: object;
  content: {
    content_type: keyof typeof contentTypes;
    data: {
      article_levels: ArticleLevel[];
    };
    id: string;
    is_lite: boolean;
  };
  currentAssignment: Assignment;
  currentLevel: ArticleLevel;
  handlePublish: () => void;
  handleSave: (arg: HandleSave) => Promise<void>;
  setCurrentLevel: (arg: ArticleLevel) => void;
  setIsAssignModalActive: (a: boolean) => void;
  shareModalProps: object;
  toggleVocabularySupport: (a: boolean) => void;
  userProperties: UserProperties;
}

const AssignModal = ({
  assignModalProps,
  content,
  currentAssignment,
  currentLevel,
  handlePublish,
  handleSave,
  setCurrentLevel,
  setIsAssignModalActive,
  shareModalProps,
  toggleVocabularySupport,
  userProperties,
}: AssignModalInterface) => {
  const { dispatch, updatedAssignment } = useAssignmentReducerContext() as {
    dispatch: () => void;
    updatedAssignment: Assignment;
  };

  const [hasAssigneeError, setHasAssigneeError] = useState<boolean>(false);
  const [resetDate, setResetDate] = useState<string | null>(null);

  const { allAssignmentsLink, assignmentLink, isPrintDisabled, printLink } =
    getShareModalValues({
      content,
      currentLevel,
      updatedAssignment,
      userProperties,
    });

  const streamProperties = {
    content_type: "Assignment",
    content_id: content.id,
    object_id: updatedAssignment.content_id || null,
  };

  const { handleSaveDraft } = useSaveDraftAssignment({
    currentContentLevelId: currentLevel.header_id,
    handleSave,
    updatedAssignment,
  });

  const assignmentMode = currentAssignmentMode(updatedAssignment);

  const isCreateMode = assignmentMode === ASSIGNMENT_MODE.CREATE;

  useEffect(() => {
    setResetDate(
      updatedAssignment?.date_goes_live || currentAssignment?.date_goes_live,
    );
  }, [currentAssignment?.date_goes_live, updatedAssignment?.date_goes_live]);

  const modalTitle = isCreateMode
    ? "Create your assignment"
    : "Assignment settings";

  const modal = (
    <>
      <InfoModal
        {...assignModalProps}
        __cssFor={{
          root: rootCss,
          modalContentWrapper: infoModalCss,
          scrollableBody: scrollableBodyCss,
        }}
        isMobileTakeover
        title={modalTitle}
        onBackgroundClick={() => setIsAssignModalActive(false)}
        labelId={modalTitle}
        footer={
          <Footer
            onClickCancel={() => setIsAssignModalActive(false)}
            onClickCreate={() => {
              setHasAssigneeError(!getHasAssignees(updatedAssignment));
              handlePublish();
            }}
            onClickSaveChanges={() => {
              setHasAssigneeError(!getHasAssignees(updatedAssignment));
              handlePublish();
            }}
            onClickSaveDraft={() => {
              handleSaveDraft();
              setIsAssignModalActive(false);
            }}
            updatedAssignment={updatedAssignment}
          />
        }
      >
        <LexileLevelSettings
          currentLevel={currentLevel}
          setContentLevel={setCurrentLevel}
          levels={content?.data?.article_levels}
        />
        <AssignmentOptions
          assignment={updatedAssignment}
          contentId={currentLevel.id}
          dispatch={dispatch}
          hasError={hasAssigneeError}
          dismissAssigneeError={() => setHasAssigneeError(false)}
          handleSaveDraft={handleSaveDraft}
        />
        <AssignmentSettings
          assignment={updatedAssignment}
          dispatch={dispatch}
          currentLevel={currentLevel}
          content={content}
          userProperties={userProperties}
          resetDate={resetDate}
          toggleVocabularySupport={toggleVocabularySupport}
        />
      </InfoModal>
      <ShareAssignmentModal
        shareModalProps={shareModalProps}
        streamProperties={streamProperties}
        updatedAssignment={updatedAssignment}
      >
        <ShareAssignmentModalContent
          allAssignmentsLink={allAssignmentsLink}
          assignmentLink={assignmentLink}
          contentId={content?.id}
          isPrintDisabled={isPrintDisabled}
          printLink={printLink}
          streamProperties={streamProperties}
        />
      </ShareAssignmentModal>
    </>
  );

  return modal;
};
export default AssignModal;
